import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getVaultDocument, updateVaultDocument, deleteVaultDocument } from '../services/ea-api'
import type { EAVaultDocumentUpdate } from '../types/ea'
import EAVendorBadge from '../components/ea/EAVendorBadge'
import EAVaultFreshnessBar from '../components/ea/EAVaultFreshnessBar'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const inputStyle = {
  background: 'rgba(30, 41, 59, 0.5)',
  border: '1px solid rgba(100, 180, 255, 0.15)',
  color: '#e2e8f0',
}

const VERIFICATION_COLORS: Record<string, string> = {
  verified: '#10b981',
  unverified: '#f59e0b',
  outdated: '#ef4444',
  pending: '#94a3b8',
}

const VERIFICATION_LABELS: Record<string, string> = {
  verified: 'Verified',
  unverified: 'Unverified',
  outdated: 'Outdated',
  pending: 'Pending',
}

const RISK_COLORS: Record<string, string> = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#ef4444',
  critical: '#dc2626',
}

export default function EAVaultDocument() {
  const { docId } = useParams<{ docId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editData, setEditData] = useState<EAVaultDocumentUpdate>({})

  const { data: doc, isLoading } = useQuery({
    queryKey: ['ea-vault-doc', docId],
    queryFn: () => getVaultDocument(docId!).then((res) => res.data),
    enabled: !!docId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: EAVaultDocumentUpdate) => updateVaultDocument(docId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea-vault-doc', docId] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteVaultDocument(docId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea-vault-docs'] })
      navigate('/ea/vault')
    },
  })

  const startEditing = () => {
    if (!doc) return
    setEditData({
      title: doc.title,
      content_markdown: doc.content_markdown,
      category: doc.category,
      subcategory: doc.subcategory || '',
      source_url: doc.source_url || '',
      fetched_date: doc.fetched_date,
      vendor_version: doc.vendor_version || '',
      verification_status: doc.verification_status,
      risk_level: doc.risk_level || '',
      tags: doc.tags,
    })
    setIsEditing(true)
  }

  const handleSave = () => {
    const payload: EAVaultDocumentUpdate = { ...editData }
    // Clean up empty strings to undefined
    if (!payload.subcategory) delete payload.subcategory
    if (!payload.source_url) delete payload.source_url
    if (!payload.vendor_version) delete payload.vendor_version
    if (!payload.risk_level) delete payload.risk_level
    updateMutation.mutate(payload)
  }

  const handleDelete = () => {
    deleteMutation.mutate()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              boxShadow: '0 0 30px rgba(139, 92, 246, 0.3)',
            }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#a78bfa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading document...</p>
        </div>
      </div>
    )
  }

  if (!doc) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            Document not found
          </h2>
          <Link to="/ea/vault" className="text-sm" style={{ color: '#60a5fa' }}>
            Back to Vault
          </Link>
        </div>
      </div>
    )
  }

  const verColor = VERIFICATION_COLORS[doc.verification_status] || '#94a3b8'
  const verLabel = VERIFICATION_LABELS[doc.verification_status] || doc.verification_status
  const riskColor = doc.risk_level ? RISK_COLORS[doc.risk_level] : undefined

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/ea/vault"
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(100, 180, 255, 0.15)',
              color: 'rgba(148, 163, 184, 0.8)',
            }}
            aria-label="Back to vault"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>
              {isEditing ? 'Edit Document' : doc.title}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              {doc.vendor} / {doc.category}
              {doc.subcategory && ` / ${doc.subcategory}`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {!isEditing ? (
            <>
              <button
                onClick={startEditing}
                className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
                  border: '1px solid rgba(96, 165, 250, 0.4)',
                  color: '#e2e8f0',
                }}
              >
                <i className="fa-solid fa-pen"></i>
                <span>Edit</span>
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105"
                style={{
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#f87171',
                }}
              >
                <i className="fa-solid fa-trash"></i>
                <span>Delete</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="px-5 py-2.5 rounded-xl text-sm font-medium transition"
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(100, 180, 255, 0.15)',
                  color: 'rgba(148, 163, 184, 0.9)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  color: '#e2e8f0',
                }}
              >
                {updateMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check"></i>
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className="rounded-2xl p-8 max-w-md w-full mx-4"
            style={{
              background: 'rgba(15, 23, 42, 0.95)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              boxShadow: '0 0 60px rgba(239, 68, 68, 0.1)',
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                }}
              >
                <i className="fa-solid fa-triangle-exclamation text-xl" style={{ color: '#f87171' }}></i>
              </div>
              <div>
                <h3 id="delete-dialog-title" className="text-lg font-bold" style={{ color: '#e2e8f0' }}>
                  Delete Document
                </h3>
                <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  This action cannot be undone
                </p>
              </div>
            </div>
            <p className="text-sm mb-6" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              Are you sure you want to delete &quot;{doc.title}&quot;? This will permanently remove the document from the vault.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-5 py-2.5 rounded-xl text-sm font-medium transition"
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(100, 180, 255, 0.15)',
                  color: 'rgba(148, 163, 184, 0.9)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed"
                style={{
                  background: 'rgba(239, 68, 68, 0.25)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#f87171',
                }}
              >
                {deleteMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-trash"></i>
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error display */}
      {updateMutation.isError && (
        <div
          className="rounded-xl p-4"
          style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}
        >
          <div className="flex space-x-3">
            <i className="fa-solid fa-exclamation-triangle mt-0.5" style={{ color: '#ef4444' }}></i>
            <div>
              <h4 className="text-sm font-semibold mb-1" style={{ color: '#ef4444' }}>
                Failed to update document
              </h4>
              <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                {(updateMutation.error as any)?.response?.data?.detail || 'An unexpected error occurred. Please try again.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl p-6" style={glassPanel}>
            {isEditing ? (
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                    Title
                  </label>
                  <input
                    type="text"
                    value={editData.title || ''}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl text-sm"
                    style={inputStyle}
                  />
                </div>

                {/* Category + Subcategory */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Category
                    </label>
                    <input
                      type="text"
                      value={editData.category || ''}
                      onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Subcategory
                    </label>
                    <input
                      type="text"
                      value={editData.subcategory || ''}
                      onChange={(e) => setEditData({ ...editData, subcategory: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Source URL + Vendor Version */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Source URL
                    </label>
                    <input
                      type="url"
                      value={editData.source_url || ''}
                      onChange={(e) => setEditData({ ...editData, source_url: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Vendor Version
                    </label>
                    <input
                      type="text"
                      value={editData.vendor_version || ''}
                      onChange={(e) => setEditData({ ...editData, vendor_version: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Fetched Date + Verification Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Fetched Date
                    </label>
                    <input
                      type="date"
                      value={editData.fetched_date || ''}
                      onChange={(e) => setEditData({ ...editData, fetched_date: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Verification Status
                    </label>
                    <select
                      value={editData.verification_status || ''}
                      onChange={(e) => setEditData({ ...editData, verification_status: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={inputStyle}
                    >
                      <option value="verified">Verified</option>
                      <option value="unverified">Unverified</option>
                      <option value="outdated">Outdated</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>

                {/* Risk Level + Tags */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Risk Level
                    </label>
                    <select
                      value={editData.risk_level || ''}
                      onChange={(e) => setEditData({ ...editData, risk_level: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={inputStyle}
                    >
                      <option value="">None</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                      Tags
                    </label>
                    <input
                      type="text"
                      placeholder="tag1, tag2, tag3"
                      value={Array.isArray(editData.tags) ? editData.tags.join(', ') : ''}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          tags: e.target.value
                            .split(',')
                            .map((t) => t.trim())
                            .filter(Boolean),
                        })
                      }
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={inputStyle}
                    />
                    <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                      Comma-separated
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                    Content (Markdown)
                  </label>
                  <textarea
                    rows={20}
                    value={editData.content_markdown || ''}
                    onChange={(e) => setEditData({ ...editData, content_markdown: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl text-sm font-mono resize-y"
                    style={inputStyle}
                  />
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-lg font-bold mb-4" style={{ color: '#e2e8f0' }}>
                  Content
                </h2>
                <div
                  className="rounded-xl p-6 overflow-auto max-h-[70vh]"
                  style={{
                    background: 'rgba(30, 41, 59, 0.4)',
                    border: '1px solid rgba(100, 180, 255, 0.08)',
                  }}
                >
                  <pre
                    className="text-sm whitespace-pre-wrap break-words font-mono leading-relaxed"
                    style={{ color: 'rgba(226, 232, 240, 0.9)' }}
                  >
                    {doc.content_markdown}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Metadata Sidebar */}
        <div className="space-y-6">
          {/* Document Info */}
          <div className="rounded-2xl p-6" style={glassPanel}>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              Document Info
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Vendor
                </p>
                <EAVendorBadge vendor={doc.vendor} size="md" />
              </div>

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Category
                </p>
                <p className="text-sm font-medium" style={{ color: '#e2e8f0' }}>
                  {doc.category}
                  {doc.subcategory && (
                    <span style={{ color: 'rgba(148, 163, 184, 0.6)' }}> / {doc.subcategory}</span>
                  )}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Slug
                </p>
                <p className="text-sm font-mono" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  {doc.slug}
                </p>
              </div>

              {doc.vendor_version && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Vendor Version
                  </p>
                  <p className="text-sm" style={{ color: '#e2e8f0' }}>
                    {doc.vendor_version}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Verification & Freshness */}
          <div className="rounded-2xl p-6" style={glassPanel}>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              Verification & Freshness
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Verification Status
                </p>
                <span
                  className="px-2.5 py-1 text-xs font-medium rounded-full"
                  style={{
                    background: `${verColor}22`,
                    color: verColor,
                    border: `1px solid ${verColor}44`,
                  }}
                >
                  {verLabel}
                </span>
              </div>

              {doc.verification_method && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Verification Method
                  </p>
                  <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                    {doc.verification_method}
                  </p>
                </div>
              )}

              {doc.last_verified_at && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Last Verified
                  </p>
                  <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                    {new Date(doc.last_verified_at).toLocaleDateString()}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Freshness
                </p>
                <div className="flex items-center space-x-2">
                  <EAVaultFreshnessBar
                    totalDocs={1}
                    staleDocs={doc.is_stale ? 1 : 0}
                    freshDocs={doc.is_stale ? 0 : 1}
                    variant="compact"
                  />
                  <span className="text-sm" style={{ color: doc.is_stale ? '#f87171' : '#34d399' }}>
                    {doc.is_stale ? 'Stale' : 'Fresh'}
                  </span>
                </div>
                <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Stale after {doc.stale_after_days} days
                </p>
              </div>

              {riskColor && doc.risk_level && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Risk Level
                  </p>
                  <span
                    className="px-2.5 py-1 text-xs font-medium rounded-full capitalize"
                    style={{
                      background: `${riskColor}22`,
                      color: riskColor,
                      border: `1px solid ${riskColor}44`,
                    }}
                  >
                    {doc.risk_level}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Source & Dates */}
          <div className="rounded-2xl p-6" style={glassPanel}>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              Source & Dates
            </h3>
            <div className="space-y-4">
              {doc.source_url && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Source URL
                  </p>
                  <a
                    href={doc.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm break-all transition-colors hover:underline"
                    style={{ color: '#60a5fa' }}
                  >
                    {doc.source_url}
                    <i className="fa-solid fa-external-link ml-1 text-xs"></i>
                  </a>
                </div>
              )}

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Fetched Date
                </p>
                <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  {new Date(doc.fetched_date).toLocaleDateString()}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Created
                </p>
                <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  {new Date(doc.created_at).toLocaleDateString()}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Last Updated
                </p>
                <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  {new Date(doc.updated_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Tags */}
          {doc.tags.length > 0 && (
            <div className="rounded-2xl p-6" style={glassPanel}>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {doc.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs font-medium rounded-lg"
                    style={{
                      background: 'rgba(100, 116, 139, 0.15)',
                      color: 'rgba(148, 163, 184, 0.8)',
                      border: '1px solid rgba(100, 116, 139, 0.25)',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Related Documents */}
          {doc.related_documents.length > 0 && (
            <div className="rounded-2xl p-6" style={glassPanel}>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                Related Documents
              </h3>
              <div className="space-y-2">
                {doc.related_documents.map((rel, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-2 rounded-lg text-sm"
                    style={{
                      background: 'rgba(30, 41, 59, 0.4)',
                      border: '1px solid rgba(100, 180, 255, 0.08)',
                      color: 'rgba(148, 163, 184, 0.7)',
                    }}
                  >
                    {rel.title || rel.id || JSON.stringify(rel)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
